import { inject, Injectable, Type } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { getModalConfig, shouldShowAsSheetModal } from 'src/app/shared/utils/modal.utils';
import { getStorageDiagnostics } from 'src/app/shared/utils/storage.utils';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameId } from 'src/app/model/game/game-id';
import { GAME_TUTORIAL_COMPONENT_MAP } from 'src/app/ui/tutorial-wizard/game-tutorials/game-tutorial-component-map';
import { GameTutorial } from 'src/app/ui/tutorial-wizard/game-tutorials/game-tutorial';

@Injectable({ providedIn: 'root' })
export class GameTutorialService {
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private analyticsService = inject(AnalyticsService);
  private roomsResultsService = inject(RoomsResultsService);
  private storageService = inject(StorageService);
  private themeService = inject(ThemeService);

  // Session tracking for demo mode
  private shownThisSession = false;

  public async tryShowTutorial(gameId: GameId): Promise<boolean> {
    const tutorialComponent = GAME_TUTORIAL_COMPONENT_MAP[gameId];
    if (!tutorialComponent) {
      return false;
    }

    const supported = this.themeService.supportedGameTutorials();
    if (!supported.includes(gameId)) {
      return false;
    }

    if (OperatorService.IsDemo) {
      return this.tryShowDemoTutorial(tutorialComponent);
    }
    return this.tryShowProductionTutorial(gameId, tutorialComponent);
  }

  private async tryShowDemoTutorial(component: Type<GameTutorial>): Promise<boolean> {
    if (this.shownThisSession) {
      return false;
    }
    this.shownThisSession = true;
    this.analyticsService.log(EventName.FeatureGameTutorialsPopupShow);
    await this.presentModal(component);
    return true;
  }

  private async tryShowProductionTutorial(
    gameId: string,
    component: Type<GameTutorial>,
  ): Promise<boolean> {
    const tutorialKey = `game-tutorial-${gameId}`;
    const flag = await this.readTutorialFlag(tutorialKey);
    if (flag.hasSeenTutorial) {
      return false;
    }

    // Use the server-side play history as an additional gate as the device-local flag above
    // cannot be trusted.
    if (this.roomsResultsService.hasPlayedBefore()) {
      return false;
    }

    // The seen-flag is device-local and unreplicated, so a player shown the same tutorial on
    // every launch is only distinguishable from a genuine first-timer by counting these events
    // per player — the storage state travels with the event so the two can be told apart.
    this.analyticsService.log(EventName.FeatureGameTutorialsPopupShow, {
      // Named distinctly from the `gameId` super property, which carries the operator's game.
      tutorialGameId: gameId,
      tutorialFlagReadFailed: flag.readFailed,
      ...(await getStorageDiagnostics(this.storageService.driverName)),
    });

    await this.markTutorialSeen(tutorialKey, gameId);
    await this.presentModal(component);
    return true;
  }

  private async readTutorialFlag(
    tutorialKey: string,
  ): Promise<{ hasSeenTutorial: boolean; readFailed: boolean }> {
    try {
      const hasSeenTutorial = await this.storageService.getItem<boolean>(tutorialKey);
      return { hasSeenTutorial: !!hasSeenTutorial, readFailed: false };
    } catch (error) {
      // Storage is unreachable (blocked third-party bucket, no usable driver), so a returning
      // player is indistinguishable from a new one. Show the tutorial rather than aborting the
      // game launch, and report it — this read is the only thing gating the tutorial.
      this.analyticsService.handleWarning(
        error,
        'gameTutorial.readFlag',
        await getStorageDiagnostics(this.storageService.driverName),
      );
      return { hasSeenTutorial: false, readFailed: true };
    }
  }

  private async markTutorialSeen(tutorialKey: string, gameId: string): Promise<void> {
    try {
      await this.storageService.setItem<boolean>(tutorialKey, true);

      // A resolved write is not proof of persistence, so read it straight back: a driver that
      // accepts and discards writes would otherwise surface only as an endlessly repeating
      // tutorial with nothing in telemetry to explain it. This catches a write dropped within
      // the session; a bucket evicted BETWEEN sessions is caught instead by repeat
      // FeatureGameTutorialsPopupShow events for the same player and game.
      const persisted = await this.storageService.getItem<boolean>(tutorialKey);
      if (!persisted) {
        this.analyticsService.handleWarning(
          new Error(`Tutorial-seen flag did not persist for ${gameId}`),
          'gameTutorial.markSeen',
          await getStorageDiagnostics(this.storageService.driverName),
        );
      }
    } catch (error) {
      // Failing to record the flag must not suppress the tutorial or fail the game launch.
      this.analyticsService.handleWarning(
        error,
        'gameTutorial.markSeen',
        await getStorageDiagnostics(this.storageService.driverName),
      );
    }
  }

  private async presentModal(tutorialComponent: Type<GameTutorial>): Promise<void> {
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    const modal = await this.modalController.create({
      component: tutorialComponent,
      ...getModalConfig(asSheetModal),
    });
    await modal.present();
    await modal.onDidDismiss();
  }
}
